import React, { useState } from 'react';
import { Modal, Form, Input, InputNumber, App } from 'antd';
import { useStableCallback } from '../../hooks/hookUtils';
import { logger } from '../../services/logger.client';
import type { NonPlayerCombatant, Combatant } from '../../types/types';

interface AddManualCombatantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddCombatant: (combatant: Omit<Combatant, 'id'> & { id?: number }) => void;
}

/**
 * Modal for manually adding a combatant to the combat tracker.
 * Allows users to enter name, HP, AC, DEX modifier, and initiative.
 *
 * Presenter component (ADR-034) -- receives onAddCombatant callback from container.
 * Uses useStableCallback (ADR-003) for the form submit handler.
 */
const AddManualCombatantModal: React.FC<AddManualCombatantModalProps> = ({ isOpen, onClose, onAddCombatant }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();

  // Using useStableCallback for the async submit handler.
  // This provides a stable reference that always accesses the latest
  // form, onAddCombatant, message, and onClose values.
  const handleOk = useStableCallback(async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const { name, hp, ac, dexMod, initiative } = values;
      const newCombatant: Omit<NonPlayerCombatant, 'id'> = {
        creatureId: 0, // Use 0 or a negative number for manually added creatures
        creatureType: 'manual' as const,
        name,
        hp,
        maxHp: hp, // Default maxHp to the entered HP
        ac: ac ?? 10, // Default AC if not provided
        dexMod: dexMod ?? 0, // Default DEX mod if not provided
        initiative: initiative ?? 0,
        statusEffects: [], // Default to empty array
        color: null, // Add the required color property
      };
      onAddCombatant(newCombatant);
      message.success(`Added "${name}" to combat.`);
      form.resetFields();
      onClose();
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'info' in error) {
        // Antd form validation error - user needs to fix form fields
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
      message.error(`Failed to add combatant: ${errorMessage}`);
      logger.warn({ formValues: form.getFieldsValue(), errorInfo: error }, 'Add Manual Combatant failed.');
    } finally {
      setLoading(false);
    }
  });

  return (
    <Modal
      title="Add Manual Combatant"
      confirmLoading={loading}
      open={isOpen}
      onCancel={onClose}
      onOk={handleOk}
      okText="Add Combatant"
      destroyOnHidden
    >      <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} name="add_combatant_form" onFinish={handleOk}>
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="e.g., Orc" />
        </Form.Item>
        <Form.Item name="hp" label="HP" rules={[{ required: true, type: 'number', min: 1 }]}>
          <InputNumber style={{ width: '100%' }} placeholder="e.g., 15" />
        </Form.Item>
        <Form.Item name="ac" label="AC">
          <InputNumber style={{ width: '100%' }} placeholder="e.g., 13" />
        </Form.Item>
        <Form.Item name="dexMod" label="DEX Mod">
          <InputNumber style={{ width: '100%' }} placeholder="e.g., 1" />
        </Form.Item>
        <Form.Item name="initiative" label="Initiative">
          <InputNumber style={{ width: '100%' }} placeholder="e.g., 12" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddManualCombatantModal;